/**
 * How each event of a saved MCE adapter is delivered: its class and member,
 * read from and written to the event-class API. The class is never stored; it
 * is the family of the message the row sends.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Input, Modal, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/lib/table";
import type { RootState } from "../../../../app/store";
import { MceEventClassListT } from "../../../../flatbuffers/generated/api/mce-event-class-list";
import { MceEventClassRowT } from "../../../../flatbuffers/generated/api/mce-event-class-row";
import { loadSchema, type ReflectionSchema } from "../../../../flatbuffers/reflection/reflection";
import GETJwtToken from "../../../../redux/authentication/authentication";
import {
  eventClassOfTemplate,
  eventKeyPath,
  fetchEventClasses,
  isOverrideOrigin,
  isUnmappedOrigin,
  originName,
  memberOptions,
  MceEventChange,
  MceEventClass,
  patchEventClasses,
  pickerValueToTemplate,
} from "../../../../utils/mceIdl/eventClasses";
import { asString } from "../../../../utils/mceIdl/messages";
import { downloadMceIdlLog } from "../../../../utils/mceIdl/mceIdlLog";
import type { MceSaveFailure } from "../../../../utils/mceIdl/saveUpload";
import { describeTemplate, MceTarget } from "../../../../utils/mceIdl/tableView";
import { Permission, hasPermission } from "../../../../utils/validatePermissions";

const COULD_NOT_CHANGE =
  "The event could not be changed. Try again. If this continues, click Get log and send the file to technical support.";
const PAGE_SIZE = 50;
const ALL = "all";

let ownSchema: Promise<ReflectionSchema> | null = null;
function lnMessageSchema(): Promise<ReflectionSchema> {
  if (!ownSchema) {
    ownSchema = import("../../../../flatbuffers/generated/front-end/front-end-message-bfbs").then(
      (generated) => loadSchema(generated.bfbsData),
    );
  }
  return ownSchema;
}

interface EventRow {
  key: string;
  row: MceEventClassRowT;
  eventId: number;
  text: string;
  family: string;
  member: string;
  source: string;
  note: string;
  canEdit: boolean;
  canReset: boolean;
  profile: MceTarget | null;
  escalator: boolean;
  /** What Edit member stays within. Null when the row sends no class it can stay in. */
  eventClass: MceEventClass | null;
}

function viewOf(row: MceEventClassRowT, schema: ReflectionSchema): EventRow {
  const effective = row.effective ? describeTemplate(schema, row.effective) : null;
  const profile = row.profile ? describeTemplate(schema, row.profile) : null;
  // No template and not unmapped: computed by a transform or ignored on purpose.
  const canEdit = effective !== null || isUnmappedOrigin(row.origin);
  return {
    key: row.key ? eventKeyPath(row.key) : "",
    row,
    eventId: row.key?.eventId ?? 0,
    text: asString(row.text),
    family: effective?.family ?? (canEdit ? "Unmapped" : "Ignored or transform"),
    member: effective?.member ?? "",
    source: originName(row.origin),
    note: asString(row.note),
    canEdit,
    canReset: isOverrideOrigin(row.origin),
    profile,
    eventClass: eventClassOfTemplate(row.effective),
    escalator: (effective ?? profile)?.path.some((part) => part.startsWith("Escalator")) ?? false,
  };
}

interface PickerState {
  eventClass: MceEventClass;
  rows: EventRow[];
}

interface MceEventClassEditorProps {
  adapterId: string;
  /** The stored mapping: when it changes, so has the list's etag. */
  storedBase64: string;
  /** The server stored a new mapping: the form must hold those bytes. */
  onStoredChanged: () => void;
}

export default function MceEventClassEditor({
  adapterId,
  storedBase64,
  onStoredChanged,
}: MceEventClassEditorProps) {
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const may = (permission: Permission) => hasPermission(permission, permissionSet ?? []);
  const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
  const [list, setList] = useState<MceEventClassListT | null>(null);
  const [schema, setSchema] = useState<ReflectionSchema | null>(null);
  const [failure, setFailure] = useState<MceSaveFailure | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [member, setMember] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [classFilter, setClassFilter] = useState(ALL);
  const [sourceFilter, setSourceFilter] = useState(ALL);
  const [query, setQuery] = useState("");

  // Only the newest read or write may set the list, or a slow read could bring back an old etag.
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    latestRequest.current += 1;
    const request = latestRequest.current;
    const token = await GETJwtToken();
    const [loaded, ownedSchema] = await Promise.all([
      fetchEventClasses(apiBase, adapterId, token),
      lnMessageSchema(),
    ]);
    if (request !== latestRequest.current) return;
    setSchema(ownedSchema);
    // A failed refresh keeps the rows on screen; the etag they hold can only give a clean 412.
    if (loaded) setList(loaded);
    else {
      setFailure({
        message: "The events could not be loaded. Reload the page and try again.",
        detail: `events: read of ${adapterId} failed`,
      });
    }
  }, [apiBase, adapterId]);

  useEffect(() => {
    load().catch((error) =>
      setFailure({
        message: "The events could not be loaded. Reload the page and try again.",
        detail: `events: ${String(error)}`,
      }),
    );
  }, [load, storedBase64]);

  const rows = useMemo(
    () => (list && schema ? list.rows.map((row) => viewOf(row, schema)) : []),
    [list, schema],
  );
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (classFilter === ALL || row.family === classFilter) &&
        (sourceFilter === ALL || row.source === sourceFilter) &&
        (!needle ||
          String(row.eventId).includes(needle) ||
          row.text.toLowerCase().includes(needle) ||
          row.member.toLowerCase().includes(needle)),
    );
  }, [rows, classFilter, sourceFilter, query]);

  const apply = async (changes: MceEventChange[]) => {
    setBusy(true);
    setFailure(null);
    latestRequest.current += 1;
    try {
      const result = await patchEventClasses(
        apiBase,
        adapterId,
        await GETJwtToken(),
        list?.cdoEtag ?? null,
        changes,
      );
      if ("failure" in result) {
        setFailure(result.failure);
        return;
      }
      latestRequest.current += 1;
      setList(result.list);
      setSelected([]);
      onStoredChanged();
    } catch (error) {
      setFailure({ message: COULD_NOT_CHANGE, detail: `events: ${String(error)}` });
    } finally {
      setBusy(false);
    }
  };

  const openPicker = (eventClass: MceEventClass, targets: EventRow[]) => {
    setMember(null);
    setPicker({ eventClass, rows: targets.filter((target) => target.canEdit) });
  };

  const confirmPicker = async () => {
    if (!picker || member === null) return;
    const changes = picker.rows.flatMap((row): MceEventChange[] => {
      const template = pickerValueToTemplate(
        picker.eventClass,
        member,
        row.escalator ? "escalator" : "elevator",
      );
      return row.row.key ? [{ key: row.row.key, template }] : [];
    });
    setPicker(null);
    await apply(changes);
  };

  const canUpdate = may(Permission.AdaptersMceEventsUpdate);
  const canDelete = may(Permission.AdaptersMceEventsDelete);
  const resets = (targets: EventRow[]) =>
    targets.flatMap((row): MceEventChange[] =>
      row.canReset && row.row.key ? [{ key: row.row.key, template: null }] : [],
    );

  const columns: ColumnsType<EventRow> = [
    { title: "Event", dataIndex: "eventId", width: 80 },
    { title: "Text", dataIndex: "text" },
    {
      title: "Class",
      dataIndex: "family",
      width: 130,
      render: (family: string) => <Tag>{family}</Tag>,
    },
    { title: "Member", dataIndex: "member" },
    { title: "Source", dataIndex: "source", width: 100 },
    { title: "Note", dataIndex: "note" },
    {
      title: "Actions",
      key: "actions",
      render: (_value, row) =>
        row.canEdit && (
          <Space size={0} wrap>
            {canUpdate && (
              <>
                <Button size="small" type="link" onClick={() => openPicker("status", [row])}>
                  Set Status
                </Button>
                <Button size="small" type="link" onClick={() => openPicker("service", [row])}>
                  Set Service
                </Button>
                {row.eventClass && (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => openPicker(row.eventClass as MceEventClass, [row])}
                  >
                    Edit member
                  </Button>
                )}
              </>
            )}
            {canDelete && row.canReset && (
              <Button
                size="small"
                type="link"
                title={row.profile ? `Back to ${row.profile.family} ${row.profile.member}` : ""}
                onClick={() => apply(resets([row]))}
              >
                Reset to default
              </Button>
            )}
          </Space>
        ),
    },
  ];

  const selectedRows = rows.filter((row) => selected.includes(row.key));
  const canAct = canUpdate || canDelete;

  return (
    <div data-testid="mce-event-class-editor">
      {list?.needsTableMode && (
        <Alert
          className="mb-2"
          type="warning"
          showIcon
          message="This adapter needs table translation on the edge"
          description="An edge running MCE in legacy or shadow mode refuses it. Set table mode for this product on the edge."
        />
      )}
      {failure && (
        <Alert
          className="mb-2"
          type="error"
          showIcon
          closable
          onClose={() => setFailure(null)}
          message={failure.message}
          action={
            <Button
              size="small"
              onClick={() =>
                downloadMceIdlLog({
                  when: new Date(),
                  action: "events",
                  product: "",
                  files: [],
                  error: new Error(failure.detail),
                })
              }
            >
              Get log
            </Button>
          }
        />
      )}
      <div className="d-flex flex-wrap gap-2 mb-2">
        <Select
          aria-label="Class"
          value={classFilter}
          onChange={setClassFilter}
          options={[
            { value: ALL, label: "All classes" },
            ...[...new Set(rows.map((row) => row.family))]
              .sort()
              .map((value) => ({ value, label: value })),
          ]}
        />
        <Select
          aria-label="Source"
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[
            { value: ALL, label: "All sources" },
            ...[...new Set(rows.map((row) => row.source))]
              .sort()
              .map((value) => ({ value, label: value })),
          ]}
        />
        <Input.Search
          allowClear
          style={{ maxWidth: 320 }}
          aria-label="Search the events"
          placeholder="Search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {canAct && selectedRows.length > 0 && (
        <Space className="mb-2" wrap>
          <span>{selectedRows.length} selected</span>
          {canUpdate && (
            <>
              <Button size="small" onClick={() => openPicker("status", selectedRows)}>
                Set Status
              </Button>
              <Button size="small" onClick={() => openPicker("service", selectedRows)}>
                Set Service
              </Button>
            </>
          )}
          {canDelete && (
            <Button size="small" onClick={() => apply(resets(selectedRows))}>
              Reset to default
            </Button>
          )}
        </Space>
      )}
      <Table
        size="small"
        rowKey="key"
        loading={busy || !list}
        columns={columns}
        dataSource={shown}
        pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
        rowSelection={
          canAct
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys.map(String)),
                getCheckboxProps: (row) => ({ disabled: !row.canEdit }),
              }
            : undefined
        }
        scroll={{ x: true }}
      />
      <Modal
        open={picker !== null}
        title={picker ? `Choose the ${picker.eventClass} member` : ""}
        okText="Set"
        okButtonProps={{ disabled: member === null }}
        onOk={confirmPicker}
        onCancel={() => setPicker(null)}
      >
        <Select
          showSearch
          className="generalStyles__width100"
          aria-label="Member"
          placeholder="Choose a member"
          optionFilterProp="label"
          value={member ?? undefined}
          onChange={setMember}
          options={picker ? memberOptions(picker.eventClass) : []}
        />
      </Modal>
    </div>
  );
}
